import { useEffect, useMemo, useState, type FormEvent } from 'react';
import PictureSentenceEditor from '../programs/PictureSentenceEditor';
import { useTrial } from '../subscription/trial';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLearner } from '../learners/LearnerContext';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useSettings } from '../settings/SettingsContext';
import {
  ALL_KINDS,
  DEVELOPER_KINDS,
  KINDS,
  resolveParams,
  violations,
  type ConceptKind,
  type ContentItem,
  type ContentPrompt,
  type ContentSequence,
  type ParamValue,
} from '../concepts/kinds';
import ParamFields, { ContentEditor, PromptEditor, SequenceEditor } from '../programs/ParamFields';
import SceneEditor from '../programs/SceneEditor';
import { StudioProgramFields } from '../studio/StudioFields';
import { useIsDeveloper } from '../studio/useIsDeveloper';
import { isKnownKind, type ProgramView } from '../programs/types';
import { addonEditor, useAddons } from '../games/host/addons';
import { CATEGORY_ORDER } from '../concepts/library';
import MediaDrawer from '../media/MediaDrawer';
import { wordsFor } from '../media/terms';
import { useT } from '../i18n/i18n';
import { programPhrases } from '../games/phrases';
import { prepareSpeech } from '../games/speak';

type Track = 'CURRENT' | 'MAINTENANCE';

export default function ProgramEditorPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  /* THE LINK STILL WINS. "Add program" carries the learner in the address, so a form opened
     for one learner keeps building for that learner even if the header moves underneath it.
     Only the fallback comes from the header, for a form reached without one. */
  const { learnerId: selected, learner } = useLearner();
  const learnerId = search.get('learner') ?? selected;
  const editing = !!id;
  const { token } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const developer = useIsDeveloper();
  useAddons();
  const t = useT();
  const { kindAllowed } = useTrial();

  const [name, setName] = useState('');
  const [track, setTrack] = useState<Track>('CURRENT');
  const [kind, setKind] = useState<ConceptKind | null>(null);
  const [category, setCategory] = useState<string>('');
  const [config, setConfig] = useState<Record<string, unknown>>({});

  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await api<ProgramView>(`/programs/${id}`, { token });
        if (cancelled) return;
        if (!isKnownKind(p.kind)) {
          setError(t('This program is a kind of game this version does not know ("{kind}").', { kind: p.kind }));
          return;
        }
        setName(p.name);
        setTrack(p.track);
        setKind(p.kind);
        setCategory(p.category ?? '');
        setConfig(p.config ?? {});
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('Failed to load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, editing, token]);

  /* THE SUPERVISOR'S DEFAULTS FOR THIS KIND, which is what an unset field
     follows. This used to read settings.defaultTileCount — a global that only
     ever made sense for a tile match. */
  const defaults = useMemo<Record<string, ParamValue>>(
    () => (kind ? { ...((settings.kindDefaults?.[kind] ?? {}) as Record<string, ParamValue>), promptRepeatSeconds: settings.promptRepeatSeconds } : {}),
    [kind, settings.kindDefaults, settings.promptRepeatSeconds],
  );

  function chooseKind(k: ConceptKind) {
    setKind(k);
    /* EMPTY, NOT SEEDED WITH THE DEFAULTS. A new program should follow the
       supervisor's settings including changes made after it was created, so it
       starts with no opinions at all. Only content is seeded, because a program
       with an empty word pool cannot be played. */
    const spec = KINDS[k].content;
    if (spec.type === 'pairs') setConfig({ items: spec.starter.map((i) => ({ ...i })) });
    else if (spec.type === 'prompts') setConfig({ prompts: spec.starter.map((p) => ({ ...p })) });
    else if (spec.type === 'sequences') {
      setConfig({ sequences: spec.starter.map((s) => ({ ...s, items: [...s.items] })) });
    } else if (spec.type === 'pictureSentences') {
      setConfig({ picture: '', sentences: [...spec.starter] });
    } else if (spec.type === 'scene') {
      const first = spec.scenes[0];
      setConfig({ scene: first.id, parts: first.parts.slice(0, spec.starterParts).map((x) => x.id) });
    } else setConfig({});
    setError(null);
  }

  function cfgNum(key: string, fallback: number): number {
    const v = config[key];
    return typeof v === 'number' ? v : fallback;
  }

  function setCfgNum(key: string, raw: string, lo: number, hi: number) {
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    setConfig((c) => ({ ...c, [key]: Math.max(lo, Math.min(hi, n)) }));
  }

  const items = Array.isArray(config.items) ? (config.items as ContentItem[]) : [];
  const prompts = Array.isArray(config.prompts) ? (config.prompts as ContentPrompt[]) : [];
  const sequences = Array.isArray(config.sequences) ? (config.sequences as ContentSequence[]) : [];
  const sceneId = typeof config.scene === 'string' ? config.scene : '';
  const sceneParts = Array.isArray(config.parts) ? (config.parts as string[]) : [];
  const picture = typeof config.picture === 'string' ? config.picture : '';
  const sentenceList = Array.isArray(config.sentences) ? (config.sentences as unknown[]).filter((s): s is string => typeof s === 'string') : [];
  const broken = kind
    ? violations(
        kind,
        resolveParams(kind, defaults, config),
        { items, prompts, sequences, scene: sceneId, parts: sceneParts, picture, sentences: sentenceList },
      )
    : [];

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError(t('Give the program a name.')); return; }
    if (!kind) { setError(t('Pick a kind of game for this program.')); return; }
    if (broken.length > 0) { setError(broken.map((m) => t(m)).join(' ')); return; }

    setBusy(true);
    try {
      const body = { name: name.trim(), kind, category: category || null, track, config };
      if (editing) await api(`/programs/${id}`, { method: 'PUT', token, body });
      else await api(
        learnerId ? `/programs?learnerId=${encodeURIComponent(learnerId)}` : '/programs',
        { method: 'POST', token, body },
      );
      void prepareSpeech(programPhrases(kind, config), settings.ttsVoice, settings.ttsRate);
      nav('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Save failed'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="muted">{t('Loading…')}</p>;

  return (
    <section aria-labelledby="ed-h">
      <div className="card">
        <h2 id="ed-h">{editing ? t('Edit program') : t('New program')}</h2>
        {error && <div className="error" role="alert">{error}</div>}

        <form className="spaced" onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="name">{t('Name')}</label>
            <input
              id="name"
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={!editing}
            />
          </div>

          {!kind ? (
            <div className="field">
              <span className="field-label">{t('Choose a kind of game')}</span>
              <ul className="template-list" aria-label={t('Kinds of game')}>
                {[...ALL_KINDS, ...(developer ? DEVELOPER_KINDS : [])].filter((k) => kindAllowed(k)).map((k) => (
                  <li key={k}>
                    <button type="button" className="template-card" onClick={() => chooseKind(k)}>
                      <span className="template-card-title">{t(KINDS[k].label)}</span>
                      <span className="template-card-desc">{t(KINDS[k].describes)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <div className="banner" role="region" aria-label={t('Selected kind of game')}>
                <strong>{t(KINDS[kind].label)}</strong>
                <span className="muted"> — {t(KINDS[kind].describes)}</span>
                {!editing && (
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => { setKind(null); setConfig({}); }}
                    style={{ marginLeft: 8 }}
                  >
                    {t('Change')}
                  </button>
                )}
              </div>

              <div className="row">
                <div className="field">
                  <label htmlFor="track">{t('List')}</label>
                  <select id="track" value={track} onChange={(e) => setTrack(e.target.value as Track)}>
                    <option value="CURRENT">{t('Current — actively learning')}</option>
                    <option value="MAINTENANCE">{t('Maintenance — review only')}</option>
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="category">{t('Category')}</label>
                  <select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">{t('No category')}</option>
                    {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{t(c)}</option>)}
                    {category && !(CATEGORY_ORDER as string[]).includes(category) && (
                      <option value={category}>{category}</option>
                    )}
                  </select>
                </div>
              </div>

              {KINDS[kind].content.type === 'pairs' && (
                <ContentEditor
                  items={items}
                  label={KINDS[kind].content.label}
                  hint={KINDS[kind].content.hint}
                  sayLabel={KINDS[kind].content.sayLabel}
                  answerLabel={KINDS[kind].content.answerLabel}
                  maxItems={KINDS[kind].content.maxItems}
                  maxItemLength={KINDS[kind].content.maxItemLength}
                  onChange={(next) => setConfig((c) => ({ ...c, items: next }))}
                />
              )}

              {KINDS[kind].content.type === 'prompts' && (
                <PromptEditor
                  prompts={prompts}
                  label={KINDS[kind].content.label}
                  hint={KINDS[kind].content.hint}
                  promptLabel={KINDS[kind].content.promptLabel}
                  hintLabel={KINDS[kind].content.hintLabel}
                  maxItems={KINDS[kind].content.maxItems}
                  maxItemLength={KINDS[kind].content.maxItemLength}
                  onChange={(next) => setConfig((c) => ({ ...c, prompts: next }))}
                />
              )}

              {KINDS[kind].content.type === 'sequences' && (
                <SequenceEditor
                  sequences={sequences}
                  label={KINDS[kind].content.label}
                  hint={KINDS[kind].content.hint}
                  sequenceLabel={KINDS[kind].content.sequenceLabel}
                  itemsLabel={KINDS[kind].content.itemsLabel}
                  maxSequences={KINDS[kind].content.maxSequences}
                  maxItems={KINDS[kind].content.maxItems}
                  maxItemLength={KINDS[kind].content.maxItemLength}
                  onChange={(next) => setConfig((c) => ({ ...c, sequences: next }))}
                />
              )}

              {(() => {
                const spec = KINDS[kind].content;
                if (spec.type !== 'scene') return null;
                return (
                  <SceneEditor
                    label={spec.label}
                    hint={spec.hint}
                    partsLabel={spec.partsLabel}
                    scenes={spec.scenes}
                    scene={sceneId}
                    parts={sceneParts}
                    onChange={(next) => setConfig((c) => ({ ...c, ...next }))}
                  />
                );
              })()}

              {(() => {
                const spec = KINDS[kind].content;
                if (spec.type !== 'pictureSentences') return null;
                return (
                  <PictureSentenceEditor
                    label={spec.label}
                    hint={spec.hint}
                    pictureLabel={spec.pictureLabel}
                    sentencesLabel={spec.sentencesLabel}
                    picture={picture}
                    sentences={sentenceList}
                    maxSentences={spec.maxSentences}
                    maxWords={spec.maxWords}
                    onChange={(next) => setConfig((c) => ({ ...c, ...next }))}
                  />
                );
              })()}

              {kind === 'studio' && <StudioProgramFields config={config} onChange={setConfig} />}
              {KINDS[kind].content.type === 'custom' && (() => {
                const Editor = addonEditor(kind);
                return Editor ? <Editor config={config} onChange={setConfig} /> : null;
              })()}

              <ParamFields
                kind={kind}
                config={config}
                defaults={defaults}
                onChange={setConfig}
              />

              {settings.features.masteryCriteria && (
                <div className="field">
                  <span className="field-label">{t('When is this learned?')}</span>
                  <div className="row">
                    <div className="field">
                      <label htmlFor="masteryPercent">{t('Right first time, at least')}</label>
                      <input
                        id="masteryPercent"
                        type="number"
                        min={1}
                        max={100}
                        value={cfgNum('masteryPercent', 80)}
                        onChange={(e) => setCfgNum('masteryPercent', e.target.value, 1, 100)}
                      />
                      <span className="field-hint">{t("Per cent of that day's tries.")}</span>
                    </div>
                    <div className="field">
                      <label htmlFor="masteryDays">{t('On this many days running')}</label>
                      <input
                        id="masteryDays"
                        type="number"
                        min={1}
                        max={30}
                        value={cfgNum('masteryDays', 3)}
                        onChange={(e) => setCfgNum('masteryDays', e.target.value, 1, 30)}
                      />
                      <span className="field-hint">{t('Days with practice. A day off is not a failure.')}</span>
                    </div>
                    <div className="field">
                      <label htmlFor="masteryMinTrials">{t('Counting days with at least')}</label>
                      <input
                        id="masteryMinTrials"
                        type="number"
                        min={1}
                        max={100}
                        value={cfgNum('masteryMinTrials', 5)}
                        onChange={(e) => setCfgNum('masteryMinTrials', e.target.value, 1, 100)}
                      />
                      <span className="field-hint">
                        {t('Tries, so one lucky answer cannot count as a whole day.')}
                      </span>
                    </div>
                  </div>
                  <span className="field-hint">
                    {t('Once this is reached, the program moves itself to the maintenance list at the end of the sitting.')}
                  </span>
                </div>
              )}

              {/* SHOWN WHILE EDITING, NOT ONLY ON SAVE. These are relationships
                  between fields — more unique items than tiles, say — and
                  finding out about them one at a time after pressing Save is
                  how a supervisor ends up guessing. */}
              {broken.length > 0 && (
                <div className="error" role="alert">
                  <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                    {broken.map((m) => <li key={m}>{t(m)}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}

          <div className="toolbar">
            <button className="btn" type="submit" disabled={busy || !kind || broken.length > 0}>
              {busy ? t('Saving…') : t('Save')}
            </button>
            <button type="button" className="btn secondary" onClick={() => nav(-1)}>{t('Cancel')}</button>
            {kind && (
              <button type="button" className="btn ghost" onClick={() => setMediaOpen(true)}>
                <span aria-hidden="true">✎</span> {t('Custom pictures and voice')}
              </button>
            )}
          </div>
        </form>
        {mediaOpen && (
          <MediaDrawer
            words={kind ? wordsFor(kind, config) : []}
            learnerId={learnerId ?? null}
            learnerName={learner && learner.id === learnerId ? learner.name : null}
            onClose={() => setMediaOpen(false)}
          />
        )}
      </div>
    </section>
  );
}
